import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Lock,
  ArrowUpDown,
  Truck,
  Share2,
  Crown,
  CheckCheck,
  MapPin,
  Phone,
  Route,
  Save,
  StickyNote,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import TopBar from "../components/TopBar";
import StatusBadge from "../components/StatusBadge";
import SoundToggle from "../components/SoundToggle";
import AppFooter from "../components/AppFooter";
import { playChime } from "../lib/notify";
import {
  api,
  API,
  formatINR,
  formatDate,
  formatDateTime,
  shortId,
  buildShareUrl,
} from "../lib/apiClient";

export default function InquiryDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortAsc, setSortAsc] = useState(true);
  const [inquiryNoteDraft, setInquiryNoteDraft] = useState("");
  const [savingInquiryNote, setSavingInquiryNote] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const prevQuoteCount = useRef(null);

  const load = async (options = {}) => {
    try {
      const res = await api.get(`/inquiries/${id}`);
      const quotes = res.data.quotes || [];
      if (
        options.silent &&
        prevQuoteCount.current !== null &&
        quotes.length > prevQuoteCount.current
      ) {
        const delta = quotes.length - prevQuoteCount.current;
        playChime();
        const latest = quotes[0];
        toast.success(
          `${delta} new quote${delta > 1 ? "s" : ""}${
            latest ? ` · ${latest.name}` : ""
          }`
        );
      }
      prevQuoteCount.current = quotes.length;
      setData(res.data);
      if (!options.silent) {
        setInquiryNoteDraft(res.data.inquiry.admin_note || "");
      }
      setNoteDrafts((prev) => {
        const next = { ...prev };
        quotes.forEach((quote) => {
          if (!options.silent || next[quote.id] === undefined) {
            next[quote.id] = quote.internal_note || "";
          }
        });
        return next;
      });
    } catch {
      if (!options.silent) toast.error("Inquiry not found");
    } finally {
      if (!options.silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll every 10s for new quotes.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") {
        load({ silent: true });
      }
    }, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <TopBar />
        <div className="max-w-7xl mx-auto p-12 text-zinc-500">Loading…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-white">
        <TopBar />
        <div className="max-w-7xl mx-auto p-12">
          <p className="text-zinc-500">Inquiry not found.</p>
          <Link
            to="/"
            className="text-[#0033CC] underline underline-offset-4 mt-3 inline-block"
          >
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const { inquiry, quotes } = data;
  const shareUrl = buildShareUrl(inquiry.share_token);
  const isLocked =
    inquiry.status === "closed" || inquiry.status === "expired";

  const sortedQuotes = [...quotes].sort((a, b) =>
    sortAsc ? a.rate - b.rate : b.rate - a.rate
  );
  const lowest = quotes.length
    ? Math.min(...quotes.map((q) => q.rate))
    : null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `Transport quote request: ${inquiry.from_city} → ${inquiry.to_city}, ${inquiry.weight} ${inquiry.weight_unit} of ${inquiry.material}. Submit your quote here: ${shareUrl}`
    );
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const saveInquiryNote = async () => {
    setSavingInquiryNote(true);
    try {
      const res = await api.patch(`/inquiries/${id}/internal-note`, {
        admin_note: inquiryNoteDraft,
      });
      setData((current) =>
        current
          ? {
              ...current,
              inquiry: { ...current.inquiry, ...res.data },
            }
          : current
      );
      setInquiryNoteDraft(res.data.admin_note || "");
      toast.success("Inquiry note saved");
    } catch {
      toast.error("Failed to save inquiry note");
    } finally {
      setSavingInquiryNote(false);
    }
  };

  const saveQuoteNote = async (quoteId) => {
    setSavingNoteId(quoteId);
    try {
      const res = await api.patch(
        `/inquiries/${id}/quotes/${quoteId}/internal-note`,
        { internal_note: noteDrafts[quoteId] || "" }
      );
      setData((current) => {
        if (!current) return current;
        return {
          ...current,
          quotes: current.quotes.map((quote) =>
            quote.id === quoteId ? { ...quote, ...res.data } : quote
          ),
        };
      });
      toast.success("Internal note saved");
    } catch {
      toast.error("Failed to save internal note");
    } finally {
      setSavingNoteId(null);
    }
  };

  const closeInquiry = async () => {
    try {
      await api.patch(`/inquiries/${id}/close`);
      toast.success("Inquiry closed");
      load();
    } catch {
      toast.error("Failed to close inquiry");
    }
  };

  const exportFile = (format) => {
    const url = `${API}/inquiries/${id}/export?format=${format}`;
    window.open(url, "_blank");
  };

  const setWinner = async (quoteId) => {
    try {
      await api.put(`/inquiries/${id}/winner`, { quote_id: quoteId });
      toast.success(
        quoteId ? "Marked as best quote" : "Best quote unmarked"
      );
      load();
    } catch {
      toast.error("Failed to update winner");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <TopBar
        right={
          <>
            <SoundToggle />
            <Link
              to="/"
              data-testid="back-link"
              className="text-sm text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </Link>
          </>
        }
      />

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="label-overline">
              Inquiry · #{shortId(inquiry.id)}
            </span>
            <StatusBadge status={inquiry.status} />
            {inquiry.winning_quote_id && (
              <span
                data-testid="header-winner-indicator"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] border border-amber-500 bg-amber-50 text-amber-900"
              >
                <Crown className="h-3 w-3" />
                Winner chosen
              </span>
            )}
          </div>
          {inquiry.internal_name && (
            <div
              data-testid="internal-name-display"
              className="mb-2 text-sm font-semibold text-zinc-900"
            >
              {inquiry.internal_name}
            </div>
          )}
          <h1 className="text-3xl md:text-5xl font-bold tracking-tighter text-zinc-900 flex items-center gap-3 flex-wrap">
            <span>{inquiry.from_city}</span>
            <span className="text-zinc-300">→</span>
            <span>{inquiry.to_city}</span>
          </h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-4 text-sm text-zinc-600">
            <div>
              <span className="label-overline mr-2">Weight</span>
              <span className="data-cell font-semibold text-zinc-900">
                {inquiry.weight} {inquiry.weight_unit}
              </span>
            </div>
            <div>
              <span className="label-overline mr-2">Material</span>
              <span className="font-semibold text-zinc-900">
                {inquiry.material}
              </span>
            </div>
            {inquiry.distance_km !== null && inquiry.distance_km !== undefined && (
              <div>
                <span className="label-overline mr-2">KM</span>
                <span className="data-cell font-semibold text-zinc-900">
                  {inquiry.distance_km}
                </span>
              </div>
            )}
            <div>
              <span className="label-overline mr-2">Created</span>
              <span className="data-cell text-zinc-900">
                {formatDate(inquiry.created_at)}
              </span>
            </div>
            <div>
              <span className="label-overline mr-2">Expires</span>
              <span className="data-cell text-zinc-900">
                {formatDate(inquiry.expires_at)}
              </span>
            </div>
          </div>
        </div>

        {(inquiry.pickup_address ||
          inquiry.drop_address ||
          inquiry.load_notes) && (
          <div className="border border-zinc-200 mb-8 bg-zinc-50/40">
            <div className="p-4 md:p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              {inquiry.pickup_address && (
                <div>
                  <div className="label-overline mb-1 flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" />
                    Pickup
                  </div>
                  <div className="text-sm text-zinc-800 leading-relaxed">
                    {inquiry.pickup_address}
                  </div>
                </div>
              )}
              {inquiry.drop_address && (
                <div>
                  <div className="label-overline mb-1 flex items-center gap-1.5">
                    <Route className="h-3 w-3" />
                    Drop
                  </div>
                  <div className="text-sm text-zinc-800 leading-relaxed">
                    {inquiry.drop_address}
                  </div>
                </div>
              )}
              {inquiry.load_notes && (
                <div>
                  <div className="label-overline mb-1 flex items-center gap-1.5">
                    <StickyNote className="h-3 w-3" />
                    Notes
                  </div>
                  <div className="text-sm text-zinc-800 leading-relaxed">
                    {inquiry.load_notes}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div
          data-testid="inquiry-admin-note-panel"
          className="border border-zinc-200 mb-8 bg-white"
        >
          <div className="p-4 md:p-5 border-b border-zinc-100 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <div className="label-overline mb-1 flex items-center gap-1.5">
                <StickyNote className="h-3 w-3" />
                Internal Admin Notes
              </div>
              <p className="text-xs text-zinc-500">
                Private running notes for your team. Transporters cannot see
                this.
              </p>
            </div>
            <div className="text-xs text-zinc-500 data-cell">
              {inquiry.admin_note_updated_at
                ? `Last saved ${formatDateTime(inquiry.admin_note_updated_at)}`
                : "Not saved yet"}
            </div>
          </div>
          <div className="p-4 md:p-5">
            <Textarea
              data-testid="inquiry-admin-note"
              value={inquiryNoteDraft}
              onChange={(e) => setInquiryNoteDraft(e.target.value)}
              placeholder="Add raw internal notes, follow-up status, transporter calls, decision context..."
              className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 min-h-[128px] text-sm"
            />
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                data-testid="save-inquiry-admin-note"
                onClick={saveInquiryNote}
                disabled={savingInquiryNote}
                className="h-10 rounded-none bg-[#0033CC] hover:bg-[#0029a8] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingInquiryNote ? "Saving" : "Save Note"}
              </Button>
            </div>
          </div>
        </div>

        {/* Share panel */}
        <div
          data-testid="share-panel"
          className="border border-zinc-900 mb-8"
        >
          <div className="p-4 md:p-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <div className="label-overline mb-2 flex items-center gap-2">
                <Share2 className="h-3 w-3" />
                Transporter Link
              </div>
              <div
                data-testid="share-url"
                className="mono text-sm md:text-[15px] break-all text-zinc-900 font-semibold"
              >
                {shareUrl}
              </div>
              <div className="text-xs text-zinc-500 mt-1.5">
                Send this to transporters. They can submit quotes without
                login. Auto-expires on {formatDate(inquiry.expires_at)}.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                data-testid="copy-link-btn"
                onClick={copyLink}
                variant="outline"
                className="h-10 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
              <Button
                data-testid="whatsapp-share-btn"
                onClick={shareWhatsApp}
                variant="outline"
                className="h-10 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
              >
                <Share2 className="h-4 w-4 mr-2" />
                WhatsApp
              </Button>
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="open-link-btn"
                className="inline-flex items-center h-10 px-3 rounded-none border border-zinc-300 hover:bg-zinc-100 text-zinc-900 text-sm font-medium"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Preview
              </a>
            </div>
          </div>
        </div>

        {/* Action bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">Quotes</h2>
            <div className="text-xs text-zinc-500 mt-0.5">
              {quotes.length} quote{quotes.length === 1 ? "" : "s"} received
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="sort-quotes-btn"
              variant="outline"
              onClick={() => setSortAsc((s) => !s)}
              className="h-10 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <ArrowUpDown className="h-4 w-4 mr-2" />
              Sort: {sortAsc ? "Lowest first" : "Highest first"}
            </Button>
            <Button
              data-testid="export-csv-btn"
              variant="outline"
              onClick={() => exportFile("csv")}
              className="h-10 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <FileText className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              data-testid="export-xlsx-btn"
              variant="outline"
              onClick={() => exportFile("xlsx")}
              className="h-10 rounded-none border-zinc-300 hover:bg-zinc-100 hover:text-zinc-900"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
            {inquiry.status === "open" && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid="close-inquiry-btn"
                    className="h-10 rounded-none bg-[#DC2626] hover:bg-[#b91c1c] text-white"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Mark Closed
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none border-2 border-zinc-900">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Close this inquiry?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Transporters with this link will no longer be able to
                      submit quotes. Existing quotes remain visible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel
                      data-testid="cancel-close-btn"
                      className="rounded-none"
                    >
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-close-btn"
                      onClick={closeInquiry}
                      className="rounded-none bg-[#DC2626] hover:bg-[#b91c1c]"
                    >
                      Close Inquiry
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {isLocked && (
          <div className="bg-zinc-100 border border-zinc-300 p-4 mb-4 text-sm flex items-center gap-3">
            <Lock className="h-4 w-4 text-zinc-600" />
            <span>
              This inquiry is <strong className="uppercase">{inquiry.status}</strong>. New quotes are not being accepted.
            </span>
          </div>
        )}

        {/* Quotes table */}
        {quotes.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-12 text-center bg-zinc-50/50">
            <Truck className="h-10 w-10 text-zinc-400 mx-auto mb-4" />
            <div className="font-semibold mb-1">No quotes yet</div>
            <p className="text-sm text-zinc-500 mb-4">
              Share the link above. Quotes will appear here as transporters
              submit them.
            </p>
          </div>
        ) : (
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[1180px]">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr className="text-left">
                  <th className="label-overline p-4">Transporter</th>
                  <th className="label-overline p-4">Phone</th>
                  <th className="label-overline p-4 text-right">Rate (₹)</th>
                  <th className="label-overline p-4">Vehicle</th>
                  <th className="label-overline p-4">Availability</th>
                  <th className="label-overline p-4">Remarks</th>
                  <th className="label-overline p-4">Internal Notes</th>
                  <th className="label-overline p-4">Submitted</th>
                  <th className="label-overline p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {sortedQuotes.map((q) => {
                  const isLowest = q.rate === lowest;
                  const isBest = inquiry.winning_quote_id === q.id;
                  return (
                    <tr
                      key={q.id}
                      data-testid={`quote-row-${q.id}`}
                      className={`border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 transition-colors ${
                        isBest
                          ? "bg-amber-50/60"
                          : isLowest
                          ? "bg-green-50/40"
                          : ""
                      }`}
                    >
                      <td className="p-4">
                        <div className="font-semibold text-zinc-900 flex items-center gap-2">
                          {q.name}
                          {isBest && (
                            <Crown className="h-3.5 w-3.5 text-amber-600" />
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {isBest && (
                            <span
                              data-testid={`best-badge-${q.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-900 bg-amber-200 border border-amber-500 px-1.5 py-0.5"
                            >
                              <Crown className="h-2.5 w-2.5" />
                              Best
                            </span>
                          )}
                          {isLowest && (
                            <span
                              data-testid={`lowest-badge-${q.id}`}
                              className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-[#16A34A] border border-[#16A34A] px-1.5 py-0.5"
                            >
                              Lowest quote
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-zinc-700">
                        <div className="inline-flex items-center gap-1.5 data-cell">
                          <Phone className="h-3.5 w-3.5 text-zinc-400" />
                          {q.phone_number || "—"}
                        </div>
                      </td>
                      <td
                        className={`p-4 text-right data-cell font-bold text-base ${
                          isBest
                            ? "text-amber-900"
                            : isLowest
                            ? "text-[#16A34A]"
                            : "text-zinc-900"
                        }`}
                      >
                        {formatINR(q.rate)}
                      </td>
                      <td className="p-4 text-zinc-700">{q.vehicle_type}</td>
                      <td className="p-4 text-zinc-700">{q.availability}</td>
                      <td className="p-4 text-zinc-600 max-w-xs">
                        <span className="line-clamp-2">
                          {q.remarks || "—"}
                        </span>
                      </td>
                      <td className="p-4 min-w-[260px]">
                        <Textarea
                          data-testid={`internal-note-${q.id}`}
                          value={noteDrafts[q.id] || ""}
                          onChange={(e) =>
                            setNoteDrafts((drafts) => ({
                              ...drafts,
                              [q.id]: e.target.value,
                            }))
                          }
                          placeholder="Internal follow-up notes..."
                          className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-zinc-900 min-h-[72px] text-xs"
                        />
                        <button
                          type="button"
                          data-testid={`save-internal-note-${q.id}`}
                          onClick={() => saveQuoteNote(q.id)}
                          disabled={savingNoteId === q.id}
                          className="mt-2 inline-flex h-8 items-center gap-1.5 border border-zinc-300 px-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-700 hover:bg-zinc-100 disabled:opacity-60"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {savingNoteId === q.id ? "Saving" : "Save"}
                        </button>
                      </td>
                      <td className="p-4 text-xs text-zinc-500 data-cell">
                        {formatDateTime(q.submitted_at)}
                      </td>
                      <td className="p-4 text-right whitespace-nowrap">
                        {isBest ? (
                          <button
                            type="button"
                            data-testid={`unmark-best-${q.id}`}
                            onClick={() => setWinner(null)}
                            className="text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-900 hover:text-amber-700 inline-flex items-center gap-1"
                          >
                            <CheckCheck className="h-3.5 w-3.5" />
                            Unmark
                          </button>
                        ) : (
                          <button
                            type="button"
                            data-testid={`mark-best-${q.id}`}
                            onClick={() => setWinner(q.id)}
                            className="text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-600 hover:text-amber-700 inline-flex items-center gap-1 border border-zinc-300 hover:border-amber-500 px-2 py-1"
                          >
                            <Crown className="h-3.5 w-3.5" />
                            Mark Best
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
      <AppFooter />
    </div>
  );
}
